import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
    getFlow,
    getPreferedPartnerId,
    setErrorcase,
    setIsLoading,
} from '../../redux/reducers/appSettings'
import {
    BasisActions,
    BasisState,
    CustomerType,
    customerTypes,
    GeneralActions,
} from '@raskrask-dk/bookingflow-state-machine'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import { useBFSM } from '../../contexts/bfsmContext'
import { BasisTemplate } from '.'
import BasisTypeState from '@raskrask-dk/bookingflow-state-machine/lib/states/basistypesState'
import { RRDate } from '@raskrask-dk/raskrask-utils'
import { BasisTypeBF } from '../../models'

const BasisTypesPage = (): ReactElement => {
    const [BFSM] = useBFSM()
    const dispatch = useAppDispatch()
    const [customerType, setCustomerType] = useBFSMValue<
        BasisState,
        BasisActions.SetCustomerType,
        'customerType'
    >('customerType', BasisActions.SetCustomerType)
    const [, setBasisType] = useBFSMValue<
        BasisState,
        BasisActions.SetBasisType,
        'basisType'
    >('basisType', BasisActions.SetBasisType)
    const address = BFSM.getData<BasisTypeState>().address
    const [basisTypes, setBasisTypes] = useState<BasisTypeBF[]>([])
    const startDate = new RRDate()
    const daysToLoad = 28
    const flow = useAppSelector(getFlow)
    const prefferedPartnerId = useAppSelector(getPreferedPartnerId)
    const availableBasisTypes = useMemo(() => {
        return basisTypes.filter((b) =>
            b.canBeBookedBy(customerType || 'Private')
        )
    }, [customerType, basisTypes])

    useEffect(() => {
        if (customerType === undefined) setCustomerType('Private')
    }, [customerType, setCustomerType])

    const onBasisTypeClick = useCallback(
        (basisType: BasisTypeBF) => {
            setBasisType(basisType)
            BFSM.doAction(BasisActions.Next, undefined)
        },
        [BFSM, setBasisType]
    )

    const onCustomerTypeClick = useCallback(
        (customerType: CustomerType) => {
            setCustomerType(customerType)
        },
        [setCustomerType]
    )

    useEffect(() => {
        dispatch(setIsLoading(true))
        try {
            const loadBasisTypes = async () => {
                const profileIds =
                    flow === 'rebook' && prefferedPartnerId
                        ? [prefferedPartnerId]
                        : undefined
                const data = await BFSM.service.getOfferedBasisTypes(
                    address,
                    startDate.toString('yyyy-MM-dd HH:mm'),
                    daysToLoad,
                    profileIds
                )
                const mappedBasisTypes = data.map(BasisTypeBF.fromDTO)
                setBasisTypes(mappedBasisTypes)
                dispatch(setIsLoading(false))
            }
            loadBasisTypes()
        } catch (err) {
            dispatch(setErrorcase('BasisTypesLoadError'))
            BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            dispatch(setIsLoading(false))
        }
    }, [])

    useEffect(() => {
        // If BasisTypes has been fetched
        if (basisTypes.length > 0) {
            const availableBasisTypes = basisTypes.filter(
                (basisType) => !basisType.disabled
            )
            // If all the basistypes are disabled:
            if (
                availableBasisTypes.length < 1 &&
                BFSM.currentState() instanceof BasisState
            ) {
                BFSM.doAction(GeneralActions.ChooseNoAvailable, undefined)
            }
        }
    }, [BFSM, basisTypes])

    return (
        <BasisTemplate
            basisTypeHandler={{
                available: availableBasisTypes,
                onClick: onBasisTypeClick,
            }}
            customerHandler={{
                selected: customerType as CustomerType,
                available: [...customerTypes],
                onClick: onCustomerTypeClick,
            }}
        />
    )
}

export default BasisTypesPage
